import { CUSTOM_MESSAGE_TYPE } from 'components/Chat/constants/messageTypes'
import React, { useEffect, useRef, useState } from 'react'
import get from 'lodash/get'
import {
  MessageDeleted as DefaultMessageDeleted,
  areMessageUIPropsEqual,
  useComponentContext,
  MessageContextValue,
  useMessageContext,
  MessageUIComponentProps,
  useChatContext,
  renderText,
} from 'stream-chat-react'
import DOMPurify from 'dompurify'
import { useFloating, autoUpdate, flip, shift, offset } from '@floating-ui/react'

import type { DefaultStreamChatGenerics } from '../../types'
import ExclamationMarkIcon from 'components/Common/SvgIcons/ExclamationMarkIcon'
import { useUserInfo } from 'hooks/useUserInfo'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials, getInitialsFromName } from 'components/Chat/helper'
import { formatFileSize } from 'utils/helper'
import copyToClipboard from 'utils/copyToClipboard'

// Import SVG icons
import { ReactComponent as ReplyIcon } from 'assets/svgs/arrow-uturn-left-icon.svg'
import { ReactComponent as ForwardIcon } from 'assets/svgs/forward-message-icon.svg'
import { ReactComponent as StarIconSvg } from 'assets/svgs/star-icon.svg'
import { ReactComponent as EditIcon } from 'assets/svgs/pencil-square.svg'
import { ReactComponent as DeleteIcon } from 'assets/svgs/trash.svg'
import { ReactComponent as ThreeDotsIconSvg } from 'assets/svgs/three-dots-icon.svg'
import { ReactComponent as PenIconSvg } from 'assets/svgs/pen-icon.svg'
import { ReactComponent as CopyIconSvg } from 'assets/svgs/copy-icon.svg'
import { ReactComponent as DownloadIcon } from 'assets/svgs/dowload-icon.svg'
import { ReactComponent as FileIcon } from 'assets/svgs/file-icon-2.svg'

import { DeleteMessageModal } from './DeleteMessageModal'
import { ForwardMessageModal } from './ForwardMessageModal'
import { EditMessageForm } from './EditMessageForm'
import { MessageOptions } from './MessageOptions'
import { SimpleReactionsList } from '../Reactions/SimpleReactionsList'
import { EmojiReactionPicker } from './EmojiReactionPicker'
import { VoiceMessage } from '../VoiceMessage'
import ReactDOM from 'react-dom'

/* =========================
   Helpers (outside component)
========================= */
const isScrollable = (el: Element) => {
  const { overflow, overflowY, overflowX } = getComputedStyle(el)
  return /(auto|scroll|overlay)/.test(`${overflow}${overflowY}${overflowX}`)
}

const getScrollParents = (node: HTMLElement | null) => {
  const parents: HTMLElement[] = []
  if (!node) return parents
  let p = node.parentElement
  while (p) {
    if (isScrollable(p)) parents.push(p)
    p = p.parentElement
  }
  return parents
}

// Body scroll lock (no layout shift)
const lockBodyScroll = () => {
  const scrollY = window.scrollY || window.pageYOffset
  const scrollbarW = window.innerWidth - document.documentElement.clientWidth
  document.body.dataset.scrollY = String(scrollY)
  if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`
  document.body.style.position = 'fixed'
  document.body.style.top = `-${scrollY}px`
  document.body.style.left = '0'
  document.body.style.right = '0'
  document.body.style.width = '100%'
  document.body.style.overflow = 'hidden'
}

const unlockBodyScroll = () => {
  const y = Number(document.body.dataset.scrollY || 0)
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.left = ''
  document.body.style.right = ''
  document.body.style.width = ''
  document.body.style.overflow = ''
  document.body.style.paddingRight = ''
  delete document.body.dataset.scrollY
  window.scrollTo(0, y)
}

// Element scroll lock (chat scroller, etc.)
type LockedEl = {
  el: HTMLElement
  prevOverflow: string
  prevOverscroll: string
  prevScrollTop: number
}
const lockElementScroll = (el: HTMLElement): LockedEl => {
  const prevOverflow = el.style.overflow
  const prevOverscroll = (el.style as any).overscrollBehavior || ''
  const prevScrollTop = el.scrollTop
  el.style.overflow = 'hidden'
  ;(el.style as any).overscrollBehavior = 'contain'
  return { el, prevOverflow, prevOverscroll, prevScrollTop }
}
const unlockElementScroll = (lock: LockedEl) => {
  lock.el.style.overflow = lock.prevOverflow
  ;(lock.el.style as any).overscrollBehavior = lock.prevOverscroll
  lock.el.scrollTop = lock.prevScrollTop
}

/* ========================= */

// Inline Portal component if not available elsewhere
const Portal = ({ children }) => {
  if (typeof window === 'undefined') return null
  return ReactDOM.createPortal(children, document.body)
}

// Three Dots Icon Component
const ThreeDotsIcon = () => <ThreeDotsIconSvg className="w-4 h-4 text-app-grey-60" />

const PenIcon = () => <PenIconSvg className="w-4 h-4 text-app-grey-60" />

const CopyIcon = () => <CopyIconSvg className="w-4 h-4 text-app-grey-60" />

// Star Icon Component wrapper to handle filled state
const StarIcon = ({ filled = false, color = 'text-app-grey-65' }) => (
  <StarIconSvg
    className={`w-5 h-5 ${color}`}
    style={{
      stroke: filled ? 'none' : 'currentColor',
      fill: filled ? 'currentColor' : 'none',
      strokeWidth: filled ? '0' : '1.25',
    }}
  />
)

// Message Actions Dropdown Menu
interface MessageActionsMenuProps {
  isVisible: boolean
  onReply: () => void
  onForward: () => void
  onPin: () => void
  onStar: () => void
  onEdit: (event: React.BaseSyntheticEvent | React.MouseEvent) => void
  onDelete: () => void
  isMyMessage: boolean
  message: any
  floatingRef?: (node: HTMLDivElement | null) => void
  floatingStyles?: React.CSSProperties
  isInThread: boolean
}

const MessageActionsMenu = ({
  isVisible,
  onReply,
  onForward,
  onPin,
  onStar,
  onEdit,
  onDelete,
  isMyMessage,
  message,
  floatingRef,
  floatingStyles,
  isInThread,
}: MessageActionsMenuProps) => {
  if (!isVisible) return null

  const isStarred = message?.pinned || false

  type MenuItemAction = (() => void) | ((e: React.MouseEvent) => void)

  const menuItems: Array<{
    icon: React.ReactNode
    label: string
    action: MenuItemAction
  }> = []

  // Only show "Reply" option when this message is not already being viewed in a thread
  if (!isInThread) {
    menuItems.push({
      icon: <ReplyIcon className="w-5 h-5 text-app-grey-65" />,
      label: 'Reply',
      action: onReply,
    })
  }

  menuItems.push(
    {
      icon: <ForwardIcon className="w-5 h-5 text-app-grey-65" />,
      label: 'Forward',
      action: onForward,
    },
    {
      icon: <StarIcon filled={isStarred} />,
      label: isStarred ? 'Unstar message' : 'Star message',
      action: onStar,
    }
  )

  // Add edit and delete for own messages
  if (isMyMessage) {
    menuItems.push(
      {
        icon: <EditIcon className="w-5 h-5 text-app-grey-70" />,
        label: 'Edit',
        action: (e: React.MouseEvent) => {
          e.preventDefault()
          e.stopPropagation()
          onEdit(e as React.BaseSyntheticEvent)
        },
      },
      {
        icon: <DeleteIcon className="w-5 h-5 text-app-grey-65" />,
        label: 'Delete',
        action: onDelete,
      }
    )
  }

  return (
    <div
      ref={floatingRef}
      style={floatingStyles}
      className="bg-app-white-100 rounded-lg shadow-lg border border-app-grey-25 py-2 min-w-[160px] z-50"
    >
      {menuItems.map((item, index) => (
        <button
          key={index}
          onClick={(e) => {
            if (item.action.length > 0) {
              ;(item.action as (e: React.MouseEvent) => void)(e)
            } else {
              ;(item.action as () => void)()
            }
          }}
          className="w-full px-4 py-2 text-left text-sm text-app-grey-70 hover:bg-app-grey-5 flex items-center gap-3"
        >
          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
            {item.icon}
          </span>
          <span className="text-app-grey-100">{item.label}</span>
        </button>
      ))}
    </div>
  )
}

// Function to highlight search terms
const highlightSearchText = (text: string, query: string) => {
  if (!query || !query.trim() || !text) return text

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedQuery})`, 'gi')
  const parts = text.split(regex)

  return parts.map((part, index) => {
    if (regex.test(part)) {
      return (
        <span key={index} className="bg-app-orange-20 text-app-orange-100 rounded font-medium">
          {part}
        </span>
      )
    }
    return part
  })
}

// (removed) detect-html helper is no longer needed because we prefer provided HTML

// Helper function to sanitize HTML and ensure links open in new tab
const sanitizeAndProcessLinks = (html: string): string => {
  // First sanitize with DOMPurify
  const sanitized = DOMPurify.sanitize(html, {
    ADD_TAGS: ['mention'],
    ADD_ATTR: ['id', 'target', 'rel', 'href'],
  })

  // Create a temporary DOM element to process links
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = sanitized

  // Find all anchor tags and add target="_blank" and rel="noopener noreferrer"
  const links = tempDiv.querySelectorAll('a')
  links.forEach((link) => {
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  })

  return tempDiv.innerHTML
}

// Custom Message Text component that formats mentions and rich text
const CustomMessageText = ({ message, isMyMessage, searchQuery }) => {
  const hasText = message.text && message.text.trim().length > 0
  const hasAttachments = message.attachments && message.attachments.length > 0
  const hasTranscript = message.custom?.transcript && message.custom.transcript.trim().length > 0
  const senderName = message.user
    ? `${message.user.firstName ?? message.user.name ?? ''} ${message.user.lastName ?? ''}`.trim()
    : ''
  const sharedDate = message.created_at
    ? new Date(message.created_at).toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : ''

  const downloadFile = async (url?: string, filename?: string) => {
    if (!url) return
    try {
      // For images, fetch as blob to ensure proper download
      const response = await fetch(url)
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      // Fallback to direct link if fetch fails (CORS issues)
      console.error('Failed to download as blob, using direct link:', error)
      const link = document.createElement('a')
      link.href = url
      link.download = filename || 'download'
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const renderImageAttachment = (attachment, idx) => (
    <div key={`image-${idx}`} className="relative group">
      <div className="relative w-[100px] h-[100px] rounded-xl overflow-hidden">
        <img
          src={attachment.image_url}
          alt={attachment.fallback || attachment.title || 'Image attachment'}
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => window.open(attachment.image_url, '_blank')}
        />

        <button
          onClick={async (e) => {
            e.preventDefault()
            e.stopPropagation()
            await downloadFile(
              attachment.image_url,
              attachment.fallback || attachment.title || `image-${idx}.jpg`
            )
          }}
          className="absolute top-2 right-2 bg-white/90 hover:bg-black/80 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all shadow-lg !text-app-black-100 hover:!text-white"
          title="Download image"
          aria-label="Download image"
        >
          <DownloadIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )

  const renderFileAttachment = (attachment, idx) => {
    const fileName = attachment.title || attachment.file_name || attachment.name || 'File'
    const fileSizeText = attachment.file_size ? formatFileSize(attachment.file_size) : ''
    const sharedMeta =
      senderName && sharedDate ? `Shared by ${senderName} on ${sharedDate}` : fileSizeText

    return (
      <div
        key={`file-${idx}`}
        className="flex items-center gap-2.5 bg-app-white-100 border border-app-grey-25 rounded-2xl p-2.5 w-full shadow-sm !text-app-black-100"
      >
        <div className="w-9 h-9 rounded-xl bg-app-primary-60 flex items-center justify-center flex-shrink-0">
          <FileIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <div className="font-semibold !text-app-black-100 truncate text-sm leading-tight">
            {fileName}
          </div>
          {sharedMeta && (
            <div className="text-xs text-app-grey-70 leading-tight mt-1">{sharedMeta}</div>
          )}
          {fileSizeText && sharedMeta !== fileSizeText && (
            <div className="text-[11px] text-app-grey-60 leading-tight mt-1">{fileSizeText}</div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.preventDefault()
            downloadFile(
              attachment.asset_url || attachment.file_url || attachment.url,
              attachment.title || attachment.file_name || 'download'
            )
          }}
          className="p-1 rounded-full bg-app-grey-5 hover:bg-app-grey-20 transition-colors text-app-primary-60"
          title="Download file"
          aria-label="Download file"
        >
          <DownloadIcon className="w-4 h-4 text-app-primary-60" />
        </button>
      </div>
    )
  }

  // Check for voice messages in attachments - more comprehensive detection
  const voiceAttachments = hasAttachments
    ? message.attachments.filter((att) => {
        if (att.type !== 'file' && att.type !== 'audio') {
          return false
        }

        const allFields = JSON.stringify(att).toLowerCase()
        if (allFields.includes('voice-message')) {
          return true
        }

        if (att.mime_type?.startsWith('audio/')) {
          return true
        }

        const fileName = att.file_name || att.name || att.title || att.fallback || att.text || ''
        if (
          typeof fileName === 'string' &&
          (fileName.endsWith('.webm') || fileName.includes('voice-message'))
        ) {
          return true
        }

        const url = att.asset_url || att.file_url || att.url || ''
        if (typeof url === 'string' && url.includes('voice-message')) {
          return true
        }

        if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio')) {
          return true
        }

        return false
      })
    : []

  const nonVoiceAttachments = hasAttachments
    ? message.attachments.filter((att) => {
        if (att.type !== 'file' && att.type !== 'audio') return true

        const allFields = JSON.stringify(att).toLowerCase()
        if (allFields.includes('voice-message')) return false

        if (att.mime_type?.startsWith('audio/')) return false

        const fileName = att.file_name || att.name || att.title || att.fallback || att.text || ''
        if (
          typeof fileName === 'string' &&
          (fileName.endsWith('.webm') || fileName.includes('voice-message'))
        )
          return false

        const url = att.asset_url || att.file_url || att.url || ''
        if (typeof url === 'string' && url.includes('voice-message')) return false

        if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio')) return false

        return true
      })
    : []

  const imageAttachments = nonVoiceAttachments.filter(
    (att) => att.type === 'image' && !!att.image_url
  )
  const fileAttachments = nonVoiceAttachments.filter((att) => att.type === 'file')

  if (!hasText && !hasAttachments && !hasTranscript) {
    return <div className="px-4 py-2 text-sm"></div>
  }

  if (voiceAttachments.length > 0) {
    return (
      <div className="px-2 py-2 text-sm text-left w-full max-w-full text-inherit">
        {/* Render voice messages */}
        <div className={imageAttachments.length > 0 || fileAttachments.length > 0 ? 'mb-2' : ''}>
          {voiceAttachments.map((attachment, index) => {
            const audioUrl = attachment.asset_url || attachment.file_url || attachment.url

            // Prepare text content to display inside voice message container
            const textContent =
              hasText || message.custom?.richTextHtml || message.html ? (
                <div className="text-left text-white [&_*]:text-white">
                  {message.custom?.richTextHtml ? (
                    <div
                      className="prose prose-sm max-w-none [&_a]:text-white [&_a:hover]:text-white/80 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-white/20 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-white [&_mention]:font-medium"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeAndProcessLinks(message.custom.richTextHtml),
                      }}
                    />
                  ) : message.html ? (
                    <div
                      className="prose prose-sm max-w-none [&_a]:text-white [&_a:hover]:text-white/80 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-white/20 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-white [&_mention]:font-medium"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeAndProcessLinks(message.html),
                      }}
                    />
                  ) : searchQuery ? (
                    <div className="text-white [&_span]:bg-yellow-300 [&_span]:text-app-black-100">
                      {highlightSearchText(message.text, searchQuery)}
                    </div>
                  ) : (
                    <div className="text-white">{renderText(message.text)}</div>
                  )}
                </div>
              ) : undefined

            return (
              <VoiceMessage
                key={`voice-${index}`}
                audioUrl={audioUrl}
                duration={message.custom?.voiceDurationSeconds}
                transcript={hasTranscript ? message.custom.transcript : undefined}
                isMyMessage={isMyMessage}
                className="mb-2 last:mb-0"
                textContent={textContent}
              />
            )
          })}
        </div>

        {/* Render non-voice attachments */}
        {(imageAttachments.length > 0 || fileAttachments.length > 0) && (
          <div className={`${hasText || hasTranscript ? 'mt-2' : ''} space-y-3`}>
            {imageAttachments.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {imageAttachments.map((attachment, idx) => renderImageAttachment(attachment, idx))}
              </div>
            )}
            {fileAttachments.length > 0 && (
              <div className="space-y-2">
                {fileAttachments.map((attachment, idx) => renderFileAttachment(attachment, idx))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // For regular messages, render with padding
  return (
    <div className="px-2 py-2 text-sm text-left w-full max-w-full text-inherit">
      {/* Render text content */}
      {(hasText || message.custom?.richTextHtml || message.html) && (
        <div className="text-left">
          {message.custom?.richTextHtml ? (
            <div
              className="prose prose-sm max-w-none [&_a]:text-app-primary-60 [&_a:hover]:text-app-primary-70 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
              dangerouslySetInnerHTML={{
                __html: sanitizeAndProcessLinks(message.custom.richTextHtml),
              }}
            />
          ) : message.html ? (
            <div
              className="prose prose-sm max-w-none [&_a]:text-app-primary-60 [&_a:hover]:text-app-primary-70 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
              dangerouslySetInnerHTML={{
                __html: sanitizeAndProcessLinks(message.html),
              }}
            />
          ) : searchQuery ? (
            highlightSearchText(message.text, searchQuery)
          ) : (
            renderText(message.text)
          )}
        </div>
      )}

      {/* Render transcript if available */}
      {hasTranscript && (
        <div className="text-left text-app-grey-70 italic">
          <span className="text-xs text-app-grey-60 mr-1">Transcript:</span>
          {message.custom.transcript}
        </div>
      )}

      {/* Render non-voice attachments */}
      {(imageAttachments.length > 0 || fileAttachments.length > 0) && (
        <div className={`${hasText || hasTranscript ? 'mt-2' : ''} space-y-3`}>
          {imageAttachments.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {imageAttachments.map((attachment, idx) => renderImageAttachment(attachment, idx))}
            </div>
          )}
          {fileAttachments.length > 0 && (
            <div className="space-y-2">
              {fileAttachments.map((attachment, idx) => renderFileAttachment(attachment, idx))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

type MessageSimpleWithContextProps<
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> = MessageContextValue<StreamChatGenerics>

const MessageSimpleWithContext = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  props: MessageSimpleWithContextProps<StreamChatGenerics> & { searchQuery?: string }
) => {
  const {
    editing,
    isMyMessage,
    message,
    clearEditingState,
    setEditingState,
    handleDelete,
    handleOpenThread,
    threadList,
    searchQuery,
  } = props
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showForwardModal, setShowForwardModal] = useState(false)
  const [showReactionSelector, setShowReactionSelector] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const messageRef = useRef<HTMLDivElement>(null)

  // Popup state
  const [showMoreActions, setShowMoreActions] = useState(false)
  const moreBtnRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const lockedParentsRef = useRef<LockedEl[]>([])

  // Floating UI for menu positioning
  const { refs, floatingStyles } = useFloating({
    open: showActionsMenu,
    onOpenChange: setShowActionsMenu,
    placement: 'bottom-end',
    middleware: [
      offset(4),
      flip({
        fallbackPlacements: ['top-end', 'bottom-start', 'top-start'],
      }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  })

  // Floating UI for emoji picker positioning
  const { refs: emojiRefs, floatingStyles: emojiFloatingStyles } = useFloating({
    open: showReactionSelector,
    placement: 'top-start',
    strategy: 'fixed',
    middleware: [
      offset(10),
      flip({
        padding: 16,
      }),
      shift({
        padding: 16,
      }),
    ],
    // No whileElementsMounted - position calculates once on open and stays fixed
  })

  const { MessageDeleted = DefaultMessageDeleted } =
    useComponentContext<StreamChatGenerics>('MessageSimple')
  const { channel, client } = useChatContext<StreamChatGenerics>()
  const { userName } = useUserInfo()

  // State to store reply users
  const [replyUsers, setReplyUsers] = React.useState<any[]>([])
  const [loadingReplyUsers, setLoadingReplyUsers] = React.useState(false)
  // Fetch reply users when component mounts or reply count changes
  React.useEffect(() => {
    const fetchReplyUsers = async () => {
      if (message.reply_count && message.reply_count > 0 && channel) {
        setLoadingReplyUsers(true)
        try {
          const replies = await channel.getReplies(message.id, { limit: 3 })
          const uniqueUsers: any[] = []
          const userIds = new Set()

          for (const reply of replies.messages) {
            const user = reply.user
            if (user && !userIds.has(user.id)) {
              userIds.add(user.id)
              uniqueUsers.push(user)
              if (uniqueUsers.length === 2) break // Only need first 2 users
            }
          }

          setReplyUsers(uniqueUsers)
        } catch (error) {
          console.error('Error fetching reply users:', error)
          setReplyUsers([])
        } finally {
          setLoadingReplyUsers(false)
        }
      } else {
        setReplyUsers([])
      }
    }

    fetchReplyUsers()
  }, [message.id, message.reply_count, channel])

  // Handle edit by using setEditingState from props
  const handleEditClick = (event: React.BaseSyntheticEvent) => {
    // Show our custom edit form
    if (setEditingState) {
      setEditingState(event)
    }
  }

  // Handle delete by showing the modal
  const handleDeleteClick = () => setShowDeleteModal(true)

  const handleCopy = async () => {
    if (message?.text) {
      try {
        await copyToClipboard(message.text)
      } catch (err) {
        console.error('Failed to copy: ', err)
      }
    }
  }
  // Close on outside click + Esc (existing behavior preserved)
  useEffect(() => {
    if (!showMoreActions) return

    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node
      if (!menuRef.current?.contains(t) && !moreBtnRef.current?.contains(t)) {
        setShowMoreActions(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowMoreActions(false)
    }

    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [showMoreActions])

  // Lock BOTH page and chat scrolling while open; reposition on resize/scroll
  useEffect(() => {
    if (!showMoreActions) {
      // unlock on close
      lockedParentsRef.current.forEach(unlockElementScroll)
      lockedParentsRef.current = []
      unlockBodyScroll()
      return
    }

    // lock page scroll
    lockBodyScroll()
    // lock all scrollable parents of the trigger (e.g., chat scroller)
    const parents = getScrollParents(moreBtnRef.current)
    lockedParentsRef.current = parents.map(lockElementScroll)

    return () => {}
  }, [showMoreActions])

  // Close menu when clicking outside
  useEffect(() => {
    if (!showActionsMenu) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const referenceElement = refs.reference.current
      const floatingElement = refs.floating.current

      if (
        floatingElement &&
        !floatingElement.contains(target) &&
        referenceElement &&
        referenceElement instanceof HTMLElement &&
        !referenceElement.contains(target)
      ) {
        setShowActionsMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showActionsMenu, refs])

  // Close emoji picker when clicking outside
  useEffect(() => {
    if (!showReactionSelector) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const referenceElement = emojiRefs.reference.current
      const floatingElement = emojiRefs.floating.current

      if (
        floatingElement &&
        !floatingElement.contains(target) &&
        referenceElement &&
        referenceElement instanceof HTMLElement &&
        !referenceElement.contains(target)
      ) {
        setShowReactionSelector(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showReactionSelector, emojiRefs])

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (handleDelete) {
      try {
        const syntheticEvent = {
          preventDefault: () => {},
          stopPropagation: () => {},
        } as React.BaseSyntheticEvent
        await handleDelete(syntheticEvent)
        setShowDeleteModal(false)
      } catch (error) {
        console.error('Failed to delete message:', error)
        setShowDeleteModal(false)
      }
    }
  }

  // Cancel delete
  const handleCancelDelete = () => setShowDeleteModal(false)

  // Reactions
  const handleSetShowEmoji = (val: any) => {
    setShowReactionSelector(val)
    return {}
  }

  // Handle emoji reaction selection
  const handleEmojiReaction = async (emoji: string) => {
    if (!channel) {
      console.error('Channel is not available')
      return
    }

    try {
      console.log('Sending reaction:', emoji, 'to message:', message.id)

      // Convert emoji to a valid Stream Chat reaction type
      // Stream Chat only accepts alphanumeric, underscore, dash, and dot
      // We'll use the emoji's unicode codepoint as the type
      const emojiCodePoint = Array.from(emoji)
        .map((char) => char.codePointAt(0)?.toString(16))
        .join('_')

      // Use Stream Chat's reaction API with the encoded emoji
      // Store the original emoji in the reaction data
      await channel.sendReaction(message.id, {
        type: `emoji_${emojiCodePoint}`,
        emoji: emoji, // Store original emoji as custom data
      })
      console.log('Reaction sent successfully')
      setShowReactionSelector(false)
    } catch (error) {
      console.error('Error adding reaction:', error)
    }
  }

  // Handle thread reply using Stream Chat's built-in functionality
  const handleReply = () => {
    if (handleOpenThread && typeof handleOpenThread === 'function') {
      // Create a synthetic event for handleOpenThread
      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
      } as React.MouseEvent

      handleOpenThread(syntheticEvent)
    }
    setShowActionsMenu(false)
  }

  const handleForward = () => {
    setShowForwardModal(true)
    setShowActionsMenu(false)
  }

  const handleForwardConfirm = async (selectedUsers: string[], selectedChannels: any[]) => {
    try {
      console.log('Forwarding message to users:', selectedUsers, 'and channels:', selectedChannels)

      // Forward to existing channels
      for (const selectedChannel of selectedChannels) {
        const forwardMessage: any = {
          text: message.text || '',
        }
        if (message.attachments && message.attachments.length > 0) {
          forwardMessage.attachments = message.attachments
        }
        // Include custom data (richTextHtml, transcript, etc.)
        if (message.custom) {
          forwardMessage.custom = message.custom
        }
        // Include html field if present
        if (message.html) {
          forwardMessage.html = message.html
        }
        await selectedChannel.sendMessage(forwardMessage)
      }

      // Forward to selected users by creating/finding DM channels
      for (const userId of selectedUsers) {
        try {
          // Create or get existing DM channel
          const channelId = [client?.userID, userId].sort().join('-')
          const dmChannel = client?.channel('messaging', channelId, {
            members: [userId, client?.userID].filter(Boolean) as string[],
            team: 'default', // You might need to adjust this based on your setup
          })

          await dmChannel?.create()
          const forwardMessage: any = {
            text: message.text || '',
          }
          if (message.attachments && message.attachments.length > 0) {
            forwardMessage.attachments = message.attachments
          }
          // Include custom data (richTextHtml, transcript, etc.)
          if (message.custom) {
            forwardMessage.custom = message.custom
          }
          // Include html field if present
          if (message.html) {
            forwardMessage.html = message.html
          }
          await dmChannel?.sendMessage(forwardMessage)
        } catch (error) {
          console.error(`Error forwarding to user ${userId}:`, error)
        }
      }

      console.log('Message forwarded successfully')
    } catch (error) {
      console.error('Error forwarding message:', error)
    }
  }

  const handleForwardCancel = () => {
    setShowForwardModal(false)
  }

  const handlePin = () => {
    // TODO: Implement pin functionality
    console.log('Pin message:', message.id)
    setShowActionsMenu(false)
  }

  const handleStar = async () => {
    try {
      if (!client) {
        console.error('Chat client not available')
        return
      }

      if (message.pinned) {
        // If message is already pinned (starred), unpin it
        await client.unpinMessage(message)
        console.log('Message unstarred (unpinned):', message.id)
      } else {
        // If message is not pinned, pin it (star it)
        await client.pinMessage(message)
        console.log('Message starred (pinned):', message.id)
      }

      // Dispatch custom event to notify other components
      window.dispatchEvent(
        new CustomEvent('chat-event', {
          detail: { type: 'message-starred', messageId: message.id, pinned: !message.pinned },
        })
      )
    } catch (error) {
      console.error('Error toggling star on message:', error)
      // You can add a toast notification here if you have one
    }
    setShowActionsMenu(false)
  }

  // Mouse event handlers for hover state
  const handleMouseLeave = () => {
    setShowActionsMenu(false)
  }

  // Handle three dots click
  const handleThreeDotsClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowActionsMenu(!showActionsMenu)
  }

  const messageClasses = isMyMessage()
    ? 'str-chat__message str-chat__message--me str-chat__message-simple str-chat__message-simple--me'
    : 'str-chat__message str-chat__message-simple'

  if (message.customType === CUSTOM_MESSAGE_TYPE.date) return null

  if (message.deleted_at || message.type === 'deleted') {
    return <MessageDeleted message={message} />
  }

  let messageDetails = message
  const isFrozen = get(channel, 'data.frozen') && message.type === 'error'
  if (isFrozen) {
    messageDetails = {
      ...message,
      text: 'Sorry, this channel has been frozen by the admin',
      type: '',
    }
  }
  return (
    <>
      <div className={`${messageClasses}`} ref={messageRef} onMouseLeave={handleMouseLeave}>
        <EditMessageForm show={editing} message={message} clearEditingState={clearEditingState} />
        {!editing && message && (
          <React.Fragment>
            <div className="mt-2 w-full relative">
              <div className={`flex gap-3 ${isMyMessage() ? 'justify-end' : 'justify-start'}`}>
                {!isMyMessage() && (
                  <AppAvatar
                    size="md"
                    type="mixed"
                    shape="Rounded"
                    text={getInitials({
                      firstName: messageDetails.user?.firstName as string,
                      lastName: messageDetails.user?.lastName as string,
                    })}
                  />
                )}
                <div
                  className={`flex flex-col w-full overflow-visible ${
                    isMyMessage() ? 'items-end' : 'items-start'
                  }`}
                >
                  <div
                    className={`text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1 ${
                      isMyMessage() ? 'mr-1 justify-end' : 'ml-1 justify-start'
                    }`}
                  >
                    <span>
                      {isMyMessage()
                        ? 'You'
                        : `${messageDetails.user?.firstName} ${messageDetails.user?.lastName}`}
                    </span>
                  </div>
                  <div className="relative">
                    {/* Check if message has voice attachments to render differently */}
                    {messageDetails.attachments?.some((att) => {
                      if (att.type !== 'file' && att.type !== 'audio') return false

                      // AGGRESSIVE DETECTION: Check for voice-message pattern in any field
                      const allFields = JSON.stringify(att).toLowerCase()
                      if (allFields.includes('voice-message')) return true

                      if (att.mime_type?.startsWith('audio/')) return true
                      const fileName =
                        att.file_name || att.name || att.title || att.fallback || att.text || ''
                      if (
                        typeof fileName === 'string' &&
                        (fileName.endsWith('.webm') || fileName.includes('voice-message'))
                      )
                        return true
                      const url = att.asset_url || att.file_url || att.url || ''
                      if (typeof url === 'string' && url.includes('voice-message')) return true
                      if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio'))
                        return true
                      return false
                    }) ? (
                      // Render voice message without background bubble
                      <div className="flex items-start gap-1">
                        {messageDetails.pinned && (
                          <div
                            className={`flex-shrink-0 mt-2 rounded-full p-1 ${
                              isMyMessage() ? 'bg-white' : 'bg-app-black-100'
                            }`}
                            title="Starred message"
                          >
                            <StarIcon
                              filled={true}
                              color={isMyMessage() ? 'text-white' : 'text-app-black-100'}
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <CustomMessageText
                            message={messageDetails}
                            isMyMessage={isMyMessage()}
                            searchQuery={searchQuery}
                          />
                        </div>
                      </div>
                    ) : (
                      // Render regular message with bubble
                      <div
                        className={`rounded-2xl flex items-start gap-1 ${
                          isMyMessage()
                            ? 'bg-app-primary-125 text-app-white-100'
                            : 'bg-app-white-95 text-app-black-100'
                        } [&_*]:text-inherit [&_*]:text-[color:inherit]`}
                      >
                        {messageDetails.pinned && (
                          <div className={`flex-shrink-0 ml-3 py-2`} title="Starred message">
                            <StarIcon
                              filled={true}
                              color={isMyMessage() ? 'text-white' : 'text-app-black-100'}
                            />
                          </div>
                        )}
                        <div>
                          <div className="flex-1">
                            <CustomMessageText
                              message={messageDetails}
                              isMyMessage={isMyMessage()}
                              searchQuery={searchQuery}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Three dots menu button - appears on hover */}
                    {/* Reaction selector */}
                    {showReactionSelector && (
                      <Portal>
                        <EmojiReactionPicker
                          onEmojiSelect={handleEmojiReaction}
                          onClose={() => setShowReactionSelector(false)}
                          floatingRef={emojiRefs.setFloating}
                          floatingStyles={emojiFloatingStyles}
                        />
                      </Portal>
                    )}

                    {/* Thread replies button - only show when there are replies */}
                    {messageDetails.reply_count && messageDetails.reply_count > 0 && !threadList ? (
                      <div className="mt-2 px-2">
                        <button
                          onClick={handleReply}
                          className={`flex items-center w-full justify-end gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-app-grey-5 rounded-lg transition-colors ${
                            isMyMessage() ? 'flex-row' : 'flex-row-reverse'
                          }`}
                        >
                          {/* User avatars - show reply users */}
                          <div className="flex -space-x-1">
                            {loadingReplyUsers ? (
                              // Loading state
                              <div className="w-6 h-6 bg-app-grey-30 rounded-full animate-pulse"></div>
                            ) : (
                              <>
                                {/* First reply user avatar */}
                                {replyUsers[0] && (
                                  <div className="relative border-2 border-white rounded-full">
                                    <AppAvatar
                                      size="xs"
                                      type="mixed"
                                      shape="Rounded"
                                      text={getInitials({
                                        firstName:
                                          replyUsers[0].firstName ||
                                          replyUsers[0].name?.split(' ')[0] ||
                                          '',
                                        lastName:
                                          replyUsers[0].lastName ||
                                          replyUsers[0].name?.split(' ')[1] ||
                                          '',
                                      })}
                                    />
                                  </div>
                                )}
                                {/* Second reply user avatar or plus badge */}
                                {messageDetails.reply_count > 1 && (
                                  <div className="relative" style={{ zIndex: 9 }}>
                                    {replyUsers[1] && messageDetails.reply_count === 2 ? (
                                      <div className="relative border-2 border-white rounded-full">
                                        <AppAvatar
                                          size="xs"
                                          type="mixed"
                                          shape="Rounded"
                                          text={getInitials({
                                            firstName:
                                              replyUsers[1].firstName ||
                                              replyUsers[1].name?.split(' ')[0] ||
                                              '',
                                            lastName:
                                              replyUsers[1].lastName ||
                                              replyUsers[1].name?.split(' ')[1] ||
                                              '',
                                          })}
                                        />
                                      </div>
                                    ) : (
                                      <div className="w-6 h-6 bg-app-grey-40 border-2 border-white rounded-full flex items-center justify-center text-[10px] font-semibold text-app-grey-80">
                                        +{messageDetails.reply_count - 1}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          <span className="font-medium text-app-grey-70 hover:text-app-grey-90">
                            {messageDetails.reply_count === 1
                              ? '1 reply'
                              : `${messageDetails.reply_count} replies`}
                          </span>
                        </button>
                      </div>
                    ) : null}

                    <div className="text-xs text-silver-darker mt-1 px-1 flex justify-end w-full">
                      <div className="flex items-center gap-2 w-full justify-end">
                        {isFrozen ? (
                          <span className="relative text-primary-error inline-block items-center mr-2 font-bold">
                            <ExclamationMarkIcon className="absolute fill-primary-error hover:fill-primary-error h-[14px] w-[14px] mr-1 inline-block left-[-18px] top-[2px]" />
                            Unsent
                          </span>
                        ) : null}

                        <div
                          className={`flex items-center justify-between  w-full mt-1 gap-2 ${
                            isMyMessage() ? 'flex-row-reverse' : 'flex-row'
                          }`}
                        >
                          {/* Action buttons - will be on right for my messages, left for others */}
                          {!isFrozen && (
                            <div
                              className={`flex items-center relative ${
                                isMyMessage() ? 'flex-row' : 'flex-row-reverse'
                              }`}
                            >
                              {isMyMessage() && (
                                <button
                                  className="mx-1"
                                  title="Edit message"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    handleEditClick(e)
                                  }}
                                >
                                  <PenIcon />
                                </button>
                              )}
                              <button
                                className="mx-1"
                                onClick={handleCopy}
                                role="menuitem"
                                title="Copy"
                              >
                                <CopyIcon />
                              </button>
                              {isMyMessage() && (
                                <button
                                  className="mx-1"
                                  onClick={handleDeleteClick}
                                  title="Delete message"
                                >
                                  <DeleteIcon className="w-5 h-5 text-app-grey-65" />
                                </button>
                              )}
                              <button
                                ref={refs.setReference}
                                className="mx-1"
                                onClick={handleThreeDotsClick}
                                title="More options"
                              >
                                <ThreeDotsIcon />
                              </button>

                              {/* Actions dropdown menu */}
                              <MessageActionsMenu
                                isVisible={showActionsMenu}
                                onReply={handleReply}
                                onForward={handleForward}
                                onPin={handlePin}
                                onStar={handleStar}
                                onEdit={handleEditClick}
                                onDelete={handleDeleteClick}
                                isMyMessage={isMyMessage()}
                                message={messageDetails}
                                floatingRef={refs.setFloating}
                                floatingStyles={floatingStyles}
                                isInThread={!!threadList}
                              />
                            </div>
                          )}

                          {/* Reactions - will be on left for my messages, right for others */}
                          <div className={`flex items-center gap-1 flex-row-reverse`}>
                            <SimpleReactionsList />
                            <MessageOptions
                              setShowEmoji={handleSetShowEmoji}
                              showEmoji={showReactionSelector}
                              emojiButtonRef={emojiRefs.setReference as any}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {isMyMessage() && (
                  <AppAvatar
                    size="md"
                    type="mixed"
                    shape="Rounded"
                    text={getInitialsFromName(userName)}
                  />
                )}
              </div>
            </div>
          </React.Fragment>
        )}
      </div>

      {/* Delete Message Modal */}
      <DeleteMessageModal
        show={showDeleteModal}
        message={message}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      {/* Forward Message Modal */}
      <ForwardMessageModal
        show={showForwardModal}
        message={message}
        onCancel={handleForwardCancel}
        onConfirm={handleForwardConfirm}
      />
    </>
  )
}

const MemoizedMessageSimple = React.memo(
  MessageSimpleWithContext,
  areMessageUIPropsEqual
) as typeof MessageSimpleWithContext

export const MessageSimple = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  props: MessageUIComponentProps<StreamChatGenerics>
) => {
  const messageContext = useMessageContext<StreamChatGenerics>('MessageSimple')
  return <MemoizedMessageSimple {...messageContext} {...props} />
}
